import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Modal } from "react-bootstrap";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import * as signalR from "@microsoft/signalr";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { FaVideo } from "react-icons/fa";
import { useGetConnections } from "../../api/queries/useGetConnections";
import { useGetMessages } from "../../api/queries/useGetMessages";
import { useAppContext } from "../../Context/AppContext";
import { Message, User } from "../../types/Types";

const ChatWindow = lazy(() => import("./ChatWindow"));
const MessageSidebar = lazy(() => import("./MessageSidebar"));

const SIDEBAR_FALLBACK_MIN_HEIGHT = 300;
const CHAT_FALLBACK_MIN_HEIGHT = 400;
const BACK_ICON_SIZE = 20;

const MAIN_CONTAINER_STYLE: React.CSSProperties = {
  height: "calc(100vh - 140px)",
  minHeight: "500px",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};
const ROW_STYLE: React.CSSProperties = { overflow: "hidden", minHeight: 0 };
const SIDEBAR_COLUMN_STYLE: React.CSSProperties = {
  backgroundColor: "#f8f9fa",
  overflow: "hidden",
  minHeight: 0,
};
const SIDEBAR_FALLBACK_STYLE: React.CSSProperties = {
  minHeight: SIDEBAR_FALLBACK_MIN_HEIGHT,
};
const CHAT_COLUMN_STYLE: React.CSSProperties = {
  overflow: "hidden",
  minHeight: 0,
};
const CHAT_FALLBACK_STYLE: React.CSSProperties = {
  minHeight: CHAT_FALLBACK_MIN_HEIGHT,
};
const MODAL_HEADER_SPAN_STYLE: React.CSSProperties = { cursor: "pointer" };

export interface Conversation {
  messages: Message[];
  id: number;
  objectId: string;
  firstName: string;
  lastName: string;
  email: string;
  lastMessageDate?: number;
}

function MessagesInner() {
  const [mobileChatOpen, setMobileChatOpen] = useState(false);
  const [selected, setSelected] = useState<Conversation | null>(null);
  const [typingUsers, setTypingUsers] = useState<{ [key: number]: boolean }>(
    {}
  );
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const { id: messageIdParam } = useParams();
  const messageId = messageIdParam ? parseInt(messageIdParam, 10) : null;
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const queryClient = useQueryClient();
  const guid = localStorage.getItem("objectId");

  const connectionRef = useRef<signalR.HubConnection | null>(null);

  const { data: userMessages } = useGetMessages({ id: guid || "" });
  const { data: userResponse } = useGetConnections({
    userId: guid || "",
  });

  const { user } = useAppContext();

  const userId = user?.user?.id;

  const users = useMemo(() => {
    return userResponse?.userDtos.filter(
      (user, index, self) =>
        index === self.findIndex((u) => u.objectId === user.objectId) &&
        user.id !== userId
    );
  }, [userResponse, userId]);

  const conversations = useMemo(() => {
    if (!users || !userMessages) return [];

    return users
      .map((user) => {
        const messages = userMessages.flatMap((item) =>
          item.messages.filter(
            (msg) =>
              msg.userIdMessageFrom === user.id ||
              msg.userIdMessageTo === user.id
          )
        );

        messages.sort(
          (a, b) =>
            new Date(a.dateSent || "").getTime() -
            new Date(b.dateSent || "").getTime()
        );

        const lastMessageDate = messages[messages.length - 1]?.dateSent
          ? new Date(messages[messages.length - 1].dateSent!).getTime()
          : 0;

        return {
          ...user,
          messages,
          lastMessageDate,
        };
      })
      .sort((a, b) => b.lastMessageDate - a.lastMessageDate);
  }, [users, userMessages]);

  const updateMessageCache = useCallback(
    (newMessage: Message) => {
      if (!guid) return;

      queryClient.setQueryData(["getMessages", guid], (oldData: any) => {
        if (!oldData) return [];

        const userPairExists = oldData.some((item: any) =>
          item.messages.some(
            (msg: Message) =>
              (msg.userIdMessageFrom === newMessage.userIdMessageFrom &&
                msg.userIdMessageTo === newMessage.userIdMessageTo) ||
              (msg.userIdMessageFrom === newMessage.userIdMessageTo &&
                msg.userIdMessageTo === newMessage.userIdMessageFrom)
          )
        );

        if (!userPairExists) {
          return [...oldData, { messages: [newMessage] }];
        }

        return oldData.map((item: any) => {
          const isSameConversation = item.messages.some(
            (msg: Message) =>
              (msg.userIdMessageFrom === newMessage.userIdMessageFrom &&
                msg.userIdMessageTo === newMessage.userIdMessageTo) ||
              (msg.userIdMessageFrom === newMessage.userIdMessageTo &&
                msg.userIdMessageTo === newMessage.userIdMessageFrom)
          );

          if (isSameConversation) {
            return {
              ...item,
              messages: [...item.messages, newMessage],
            };
          }
          return item;
        });
      });
    },
    [guid, queryClient]
  );

  const getAccessToken = useCallback(async (): Promise<string> => {
    if (accounts.length === 0) throw new Error("No accounts found");

    try {
      const response = await instance.acquireTokenSilent({
        scopes: ["https://smbfglobal.onmicrosoft.com/user-api/user.read"],
        account: accounts[0],
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        const response = await instance.acquireTokenPopup({
          scopes: ["https://smbfglobal.onmicrosoft.com/user-api/user.read"],
        });
        return response.accessToken;
      }
      throw error;
    }
  }, [instance, accounts]);

  useEffect(() => {
    if (!userResponse || connectionRef.current) return;

    const connect = async () => {
      try {
        const token = await getAccessToken();

        const newConnection = new signalR.HubConnectionBuilder()
          .withUrl(
            "https://smbfglobal-g6bbhufugxchfncw.uksouth-01.azurewebsites.net/chatHub",
            {
              accessTokenFactory: async () => token,
            }
          )
          .withAutomaticReconnect()
          .configureLogging(signalR.LogLevel.Warning)
          .build();

        newConnection.on("ReceiveMessage", (message: any) => {
          const newMsg: Message = {
            id: Math.random(),
            body: message.body,
            dateSent: message.dateSent || new Date().toISOString(),
            userIdMessageFrom: message.senderId,
            userIdMessageTo: message.receiverId,
          };
          updateMessageCache(newMsg);
        });

        newConnection.on("usertyping", (senderObjectId: string) => {
          const sender = userResponse.userDtos.find(
            (u) => u.objectId === senderObjectId
          );
          if (sender) {
            setTypingUsers((prev) => ({ ...prev, [sender.id]: true }));
          }
        });

        newConnection.on("userstoppedtyping", (senderObjectId: string) => {
          const sender = userResponse.userDtos.find(
            (u) => u.objectId === senderObjectId
          );
          if (sender) {
            setTypingUsers((prev) => ({ ...prev, [sender.id]: false }));
          }
        });

        await newConnection.start();
        connectionRef.current = newConnection;
      } catch (err) {
        console.error("SignalR connection error:", err);
      }
    };

    connect();

    return () => {
      connectionRef.current?.stop();
    };
  }, [userResponse, getAccessToken, updateMessageCache]);

  useEffect(() => {
    if (!messageId || !conversations.length) return;

    const matched = conversations.find((c) => c.id === messageId);
    if (matched) {
      setSelected(matched);
      if (window.innerWidth < 768) setMobileChatOpen(true);
    }
  }, [messageId, conversations]);

  useEffect(() => {
    if (!selected || !conversations.length) return;
    const updated = conversations.find((c) => c.id === selected.id);
    if (updated && updated !== selected) {
      setSelected(updated);
    }
  }, [conversations, selected]);

  useEffect(() => {
    const handleMessageSent = (e: any) => {
      const { newMessage } = e.detail;
      updateMessageCache(newMessage);
    };

    window.addEventListener("messageSent", handleMessageSent);
    return () => window.removeEventListener("messageSent", handleMessageSent);
  }, [updateMessageCache]);

  const handleSelect = useCallback(
    (conversation: Conversation) => {
      setSelected(conversation);
      navigate(`/messages/${conversation.id}`);
      if (window.innerWidth < 768) setMobileChatOpen(true);
    },
    [navigate]
  );

  const handleUserClick = useCallback((user: User) => {
    setSelectedUser(user);
    setShowProfileModal(true);
  }, []);

  const handleVideoCallClick = useCallback(() => {
    if (!selected?.objectId) return;
    const videoCallUrl = `/video-call/${selected.objectId}`;
    window.open(videoCallUrl, "_blank", "noopener,noreferrer");
  }, [selected?.objectId]);

  const handleCloseMobileChat = useCallback(() => {
    setMobileChatOpen(false);
    setSelected(null);
    navigate("/messages");
  }, [navigate]);

  const handleModalHeaderNameClick = useCallback(() => {
    const user = userResponse?.userDtos.find((u) => u.id === selected?.id);
    const imageEntry = userResponse?.userImageUrls?.find(
      (img) => img.userId === user?.id
    );
    if (user && handleUserClick && imageEntry) {
      handleUserClick({ ...user, ...imageEntry?.imageUrls });
    }
  }, [userResponse, selected?.id, handleUserClick]);

  return (
    <main className="page-content p-4 pt-5 pt-md-5 m-0" aria-label="Messages">
      <div
        className="mx-auto shadow rounded messages-card"
        style={MAIN_CONTAINER_STYLE}
      >
        <div className="d-flex px-3 px-md-4 py-3 gradient-bg text-white align-items-center rounded-top flex-shrink-0">
          <h4 className="mb-0">Messages</h4>
        </div>

        <div className="row flex-grow-1 m-0 g-0 rounded-bottom" style={ROW_STYLE}>
          {conversations.length > 0 ? (
            <>
              <div
                className={`col-12 col-md-4 border-end p-0 d-flex flex-column ${
                  selected && mobileChatOpen ? "d-none d-md-flex" : ""
                }`}
                style={SIDEBAR_COLUMN_STYLE}
              >
                <Suspense
                  fallback={
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={SIDEBAR_FALLBACK_STYLE}
                      aria-hidden
                    >
                      <span className="text-muted">Loading…</span>
                    </div>
                  }
                >
                  <MessageSidebar
                    conversations={conversations}
                    selected={selected}
                    onSelect={handleSelect}
                    typingUsers={typingUsers}
                  />
                </Suspense>
              </div>

              <div className="col-12 col-md-8 p-0 d-none d-md-flex flex-column" style={CHAT_COLUMN_STYLE}>
                {selected ? (
                  <Suspense
                    fallback={
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={CHAT_FALLBACK_STYLE}
                        aria-hidden
                      >
                        <span className="text-muted">Loading…</span>
                      </div>
                    }
                  >
                  <ChatWindow
                    users={userResponse}
                    handleUserClick={handleUserClick}
                    setSelectedUser={setSelectedUser}
                    setShowProfileModal={setShowProfileModal}
                    showProfileModal={showProfileModal}
                    selectedUser={selectedUser}
                    userId={userId}
                    conversation={selected}
                    connection={connectionRef.current}
                    isTyping={!!typingUsers[selected.id]}
                  />
                  </Suspense>
                ) : (
                  <div className="d-flex justify-content-center align-items-center h-100 text-muted p-4">
                    Select a conversation to start chatting
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="d-flex justify-content-center align-items-center h-100 text-muted p-4">
              Please connect with a user to start a conversation
            </div>
          )}
        </div>
      </div>

      <Modal
        show={mobileChatOpen}
        onHide={handleCloseMobileChat}
        fullscreen
        backdrop="static"
        className="d-md-none"
      >
        <Modal.Header className="border-bottom d-flex align-items-center justify-content-between px-3 py-2">
          <div className="d-flex align-items-center flex-grow-1 min-w-0">
            <Button
              variant="link"
              className="text-decoration-none text-dark p-0 me-2 flex-shrink-0"
              onClick={handleCloseMobileChat}
            >
              <img
              src="/back.png"
              alt="Back to messages"
              width={BACK_ICON_SIZE}
              height={BACK_ICON_SIZE}
              loading="lazy"
              decoding="async"
              fetchPriority="low"
            />
            </Button>

            <span
              className="fw-medium text-truncate"
              style={MODAL_HEADER_SPAN_STYLE}
              onClick={handleModalHeaderNameClick}
            >
              {selected?.firstName + " " + selected?.lastName}
            </span>
          </div>
          {user?.user?.isAdmin === 1 && (
            <Button
              variant="link"
              className="text-decoration-none text-dark p-0 flex-shrink-0"
              onClick={handleVideoCallClick}
            >
              <FaVideo size={20} />
            </Button>
          )}
        </Modal.Header>
        <Modal.Body className="p-0">
          {selected && (
            <Suspense
              fallback={
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={CHAT_FALLBACK_STYLE}
                  aria-hidden
                >
                  <span className="text-muted">Loading…</span>
                </div>
              }
            >
              <ChatWindow
                userId={userId}
                conversation={selected}
                connection={connectionRef.current}
                isTyping={!!typingUsers[selected.id]}
              />
            </Suspense>
          )}
        </Modal.Body>
      </Modal>
    </main>
  );
}

MessagesInner.displayName = "Messages";

export default memo(MessagesInner);
